import React, { createContext, useContext, useEffect, useState } from 'react';
import { authApi } from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== 'undefined' && window.location.hash && window.location.hash.includes('session_id=')) {
      setLoading(false);
      return;
    }
    // First try cookie-based session (Google auth)
    authApi.me()
      .then((u) => setUser(u))
      .catch(() => {
        // Then try Bearer JWT (email/password auth)
        const token = localStorage.getItem('mft_token');
        if (!token) return;
        // authApi.me already uses interceptor to add Bearer; try again explicitly (same call — interceptor fires either way)
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    try {
      const { token, user } = await authApi.login(email, password);
      localStorage.setItem('mft_token', token);
      setUser(user);
      return { success: true };
    } catch (e) {
      return { success: false, error: e?.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (email, password) => {
    try {
      const { token, user } = await authApi.register(email, password);
      localStorage.setItem('mft_token', token);
      setUser(user);
      return { success: true };
    } catch (e) {
      return { success: false, error: e?.response?.data?.detail || 'Registration failed' };
    }
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/discover';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSessionId = async (session_id) => {
    const { user: u } = await authApi.emergentSession(session_id);
    setUser(u);
    return u;
  };

  const logout = async () => {
    try { await authApi.logout(); } catch (e) { /* ignore */ }
    localStorage.removeItem('mft_token');
    setUser(null);
  };

  const refresh = async () => {
    try {
      const u = await authApi.me();
      setUser(u);
    } catch (e) { /* ignore */ }
  };

  const updateUser = (patch) => setUser((u) => ({ ...(u || {}), ...patch }));

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, updateUser, loginWithGoogle, handleSessionId }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
